import type { Registry } from '../content/registry';
import type { Place } from '../content/types';
import type { Fields } from './fields';
import type { RoadSource } from './path-adapter';
import { hash, hash01, hashStr, Rng } from './rng';
import type { PathNet } from './paths';

const CELL = 512;
/** how far apart the samples are when a repeating place tests its ground */
const PROBE = 12;

/** Spatial index over content/places so a chunk can find the places touching it cheaply. */
export class PlaceIndex {
  private cells = new Map<string, Place[]>();
  /** places that stand once, where they were authored */
  readonly all: Place[];
  /** (owner, Sep 14) places that repeat on their own grid: one copy a cell, where the ground allows */
  private readonly repeating: Place[];
  /** a repeating place's copy for one of its cells, or null when nothing fitted there */
  private copies = new Map<string, Place | null>();

  constructor(reg: Registry, private seed: number, private fields?: Fields, private net?: RoadSource) {
    const list = reg.all('place').filter((p) => p.seed === undefined || p.seed === seed);
    this.all = list.filter((p) => !p.every);
    this.repeating = list.filter((p) => !!p.every);
    for (const p of this.all) {
      const cx0 = Math.floor(p.x / CELL), cy0 = Math.floor(p.y / CELL), cx1 = Math.floor((p.x + p.w - 1) / CELL), cy1 = Math.floor((p.y + p.h - 1) / CELL);
      for (let cy = cy0; cy <= cy1; cy++) for (let cx = cx0; cx <= cx1; cx++) {
        const k = `${cx},${cy}`;
        let arr = this.cells.get(k);
        if (!arr) { arr = []; this.cells.set(k, arr); }
        arr.push(p);
      }
    }
  }

  /**
   * (owner, Sep 14: "add them with proper space for planes to land safely") the copy of a repeating place in one cell of its grid.
   * A handful of jittered spots are tried; the first whose `clear` part is open buildable land — no water, no town, no highway
   * corridor through it — wins, and the cell keeps nothing when none is. Pure in (seed, cell), so every machine agrees.
   */
  /**
   * (perf, Sep 17 — docs/PERF.md) the probe costs half a second a cell and built network cells kilometres from anything on screen: it
   * runs only where a chunk is generated (`probe`), and a main thread with generator workers learns the copies from their chunks
   * (`learn`) instead of probing for the map, the search and the spawns. A cell nobody has generated in yet is simply not known.
   */
  /** (owner, Sep 25) one repeating place's copy in one cell of its grid, probed if need be — for the tools that go looking (tools/find-place.ts) */
  copyOf(id: string, gx: number, gy: number): Place | null { const p = this.repeating.find((q) => q.id === id); return p ? this.copyAt(p, gx, gy) : null; }
  learn(copies: readonly Place[]): void { for (const c of copies) if (c.cell && !this.copies.has(c.cell)) this.copies.set(c.cell, c); }
  private copyAt(p: Place, gx: number, gy: number, probe = true): Place | null {
    const key = `${p.id}:${gx},${gy}`;
    const had = this.copies.get(key);
    if (had !== undefined) return had;
    if (!probe) return null; // (not known here, and not ours to find out)
    let out: Place | null = null;
    const f = this.fields;
    if (f) {
      const rng = new Rng(hash(this.seed, gx, gy, hashStr(p.id, 0)));
      const clear = p.clear ?? { x: 0, y: 0, w: p.w, h: p.h };
      if (p.atTown) {
        // (owner, Sep 16: a landfill "one per town on its edge") the cell's town, when it has one: the copy stands just past the
        // outskirts (1.6 × the radius is where `openGround` stops calling it town) in a hashed direction, twelve tries round the clock
        const s = f.settlements.at(gx, gy);
        if (s) {
          const a0 = rng.float() * Math.PI * 2, r = s.radius * 1.6 + 12 + Math.max(p.w, p.h) / 2;
          for (let tryN = 0; tryN < 12 && !out; tryN++) {
            const a = a0 + (tryN * Math.PI * 2) / 12;
            const x = Math.round(s.x + Math.cos(a) * r - p.w / 2), y = Math.round(s.y + Math.sin(a) * r - p.h / 2);
            if (this.openGround(f, x, y, p.w, p.h) && this.noRoad(x + clear.x, y + clear.y, clear.w, clear.h)) out = { ...p, x, y, cell: key };
          }
        }
      } else if (p.remote) out = this.remoteSpot(p, f, gx, gy, key);
      else for (const { x, y } of this.jittered(p, gx, gy)) {
        // the whole rect has to be land the place can stand on; the `clear` part — a runway — has to have no road across it
        if (this.openGround(f, x, y, p.w, p.h) && this.noRoad(x + clear.x, y + clear.y, clear.w, clear.h)) { out = { ...p, x, y, cell: key }; break; }
      }
    }
    if (this.copies.size > 4096) this.copies.clear();
    this.copies.set(key, out);
    return out;
  }

  /** the spots a jittered repeating place tries in one cell, in order (the first on open ground wins) */
  private jittered(p: Place, gx: number, gy: number): { x: number; y: number }[] {
    const every = p.every!, rng = new Rng(hash(this.seed, gx, gy, hashStr(p.id, 0)));
    const room = { x: Math.max(0, every - p.w - 16), y: Math.max(0, every - p.h - 16) };
    const out: { x: number; y: number }[] = [];
    for (let tryN = 0; tryN < 8; tryN++) { const x = gx * every + 8 + rng.int(0, room.x), y = gy * every + 8 + rng.int(0, room.y); out.push({ x, y }); }
    return out;
  }

  /**
   * (owner, Sep 25: "easter egg: very rare place: area 69. heavily guarded big area surrounded by no roads … placed very rare and
   * not near any cities") The copy of a `remote` place in one cell of its (big) grid: the most remote ground in it. The towns sit one
   * a settlement cell (1024 tiles) seven cells in ten, so the country far from all of them is the middle of an empty one; the trunk
   * roads run every 512 tiles (paths.ts level 0), so the ground far from every road is the middle of one of the four squares they
   * cut such a cell into. Every such square in the cell is a candidate: the ones whose middle is in the wanted biome and whose rect
   * stays `townGap` past every town's rim are tried most remote first, and the first on open ground with no road or railway within
   * `roadGap` of it wins. The cheap tests (towns, biome) go first — the road test builds the network there, and only a handful of
   * candidates ever reach it. Pure in (seed, cell), like every copy.
   */
  private remoteSpot(p: Place, f: Fields, gx: number, gy: number, key: string): Place | null {
    const every = p.every!, rm = p.remote!, S = f.settlements, SC = 1024, TRUNK = 512, SNAP = 64;
    // (owner: "very rare") only `share` of the cells are looked at at all — decided by the cell's hash before anything is sampled,
    // so a cell that is not one costs nothing
    if (rm.share !== undefined && hash01(this.seed + hashStr(p.id, 0), gx, gy) >= rm.share) return null;
    const cands: { x: number; y: number; gap: number }[] = [];
    const cx0 = Math.ceil((gx * every) / SC), cx1 = Math.floor(((gx + 1) * every) / SC) - 1, cy0 = Math.ceil((gy * every) / SC), cy1 = Math.floor(((gy + 1) * every) / SC) - 1;
    for (let cy = cy0; cy <= cy1; cy++) for (let cx = cx0; cx <= cx1; cx++) {
      if (S.at(cx, cy)) continue;
      for (const [ox, oy] of [[0, 0], [1, 0], [0, 1], [1, 1]]) {
        // the middle of a trunk square: the trunk's nodes sit near the middle of every eighth 64-cell, 32 tiles past the 512 line
        const mx = cx * SC + ox * TRUNK + TRUNK / 2 + 32, my = cy * SC + oy * TRUNK + TRUNK / 2 + 32;
        const x = Math.round((mx - p.w / 2) / SNAP) * SNAP, y = Math.round((my - p.h / 2) / SNAP) * SNAP; // (on the chunk grid: the layout knows where its chunks' edges fall)
        let gap = Infinity;
        for (let j = -3; j <= 3; j++) for (let i = -3; i <= 3; i++) { const s = S.at(cx + i, cy + j); if (s) gap = Math.min(gap, Math.hypot(s.x - (x + p.w / 2), s.y - (y + p.h / 2)) - s.radius - Math.hypot(p.w, p.h) / 2); }
        if (gap < rm.townGap) continue;
        if (rm.biomes && ![[0, 0], [p.w, 0], [0, p.h], [p.w, p.h], [p.w / 2, p.h / 2]].every(([dx, dy]) => rm.biomes!.includes(f.biomeAt(x + dx, y + dy).id))) continue;
        cands.push({ x, y, gap });
      }
    }
    cands.sort((a, b) => b.gap - a.gap || a.x - b.x || a.y - b.y);
    for (const c of cands.slice(0, 16)) {
      if (!this.roadFree(c.x - rm.roadGap, c.y - rm.roadGap, p.w + rm.roadGap * 2, p.h + rm.roadGap * 2) || !this.openGround(f, c.x, c.y, p.w, p.h)) continue; // (the roads first: the sketch answers in a few ms, the ground's samples cost more)
      // never over another place: an authored one, or a spot a jittered repeating one might take (an airstrip wants the same open
      // ground) — any of its tries over the rect rules the candidate out, which costs no probe of it (half a second each). A town-edge
      // copy (the landfill) stands within 0.6 of a radius and its own size of a rim, well inside any `townGap` worth the name
      const over = (o: { x: number; y: number; w: number; h: number }) => o.x < c.x + p.w && o.x + o.w > c.x && o.y < c.y + p.h && o.y + o.h > c.y;
      const tried = (o: Place) => { const e = o.every!; for (let ty = Math.floor((c.y - o.h) / e); ty <= Math.floor((c.y + p.h) / e); ty++) for (let tx = Math.floor((c.x - o.w) / e); tx <= Math.floor((c.x + p.w) / e); tx++) if (this.jittered(o, tx, ty).some((t) => over({ x: t.x, y: t.y, w: o.w, h: o.h }))) return true; return false; };
      if (this.all.some(over) || this.repeating.some((o) => o !== p && !o.remote && !o.atTown && tried(o))) continue;
      return { ...p, x: c.x, y: c.y, cell: key };
    }
    return null;
  }

  /**
   * (owner, Sep 25: "surrounded by no roads") no road and no railway anywhere in this rect. On the path world the network answers
   * from its sketch without building a cell (`PathNet.roadsMayReach`, conservative: a spot it doubts is simply not taken); on the
   * lattice a grid of lines 8 tiles apart asks `hitsAt`.
   */
  private roadFree(x: number, y: number, w: number, h: number): boolean {
    const net = this.net; if (!net) return true;
    const pn = (net as { net?: PathNet }).net;
    if (pn) return !pn.roadsMayReach(x, y, x + w, y + h);
    const hits: ReturnType<RoadSource['hitsAt']> = [];
    for (let ty = y; ty <= y + h; ty += 8) for (let tx = x; tx <= x + w; tx += 2) if (net.hitsAt(tx, ty, hits).length) return false;
    for (let tx = x; tx <= x + w; tx += 8) for (let ty = y; ty <= y + h; ty += 2) if (net.hitsAt(tx, ty, hits).length) return false;
    return true;
  }

  /** is this rect flat open land a runway could sit on: buildable everywhere, no town, no highway corridor across it */
  private openGround(f: Fields, x: number, y: number, w: number, h: number): boolean {
    // a coarse pass first: nearly every spot that fails does so on water or a town, and a dozen samples find that — the full
    // grid below is a few hundred, and a search over a region would pay it for every empty cell
    for (let j = 0; j <= 6; j++) for (let i = 0; i <= 6; i++) {
      const tx = x + Math.round((w * i) / 6), ty = y + Math.round((h * j) / 6);
      const t = f.terrainAt(tx, ty);
      if (!t.buildable || !t.walkable || f.settlements.in(tx, ty)) return false;
    }
    for (let ty = y; ty <= y + h; ty += Math.min(PROBE, h)) for (let tx = x; tx <= x + w; tx += Math.min(PROBE, w)) {
      const t = f.terrainAt(tx, ty);
      if (!t.buildable || !t.walkable) return false; // (water, marsh, bare rock: nothing to roll on)
      if (f.settlements.in(tx, ty)) return false; // (a town: its streets and lots own the ground)
    }
    // a highway corridor through it would cut the place in two (the place keeps level-0 roads)
    for (const c of f.settlements.corridorsNear(x + w / 2, y + h / 2)) {
      const near = c.axis === 'v' ? c.pos >= x - 24 && c.pos <= x + w + 24 && c.from <= y + h && c.to >= y : c.pos >= y - 24 && c.pos <= y + h + 24 && c.from <= x + w && c.to >= x;
      if (near) return false;
    }
    return true;
  }

  /**
   * (owner, Sep 14: "proper space for planes to land safely") no road across this rect. A place keeps the level-0 network
   * (`roads: "major"`), so a trunk road over the runway would stay there — the spot is refused instead. Three lines are walked
   * rather than the whole area (the middle and the two edges, every third tile): anything crossing the strip meets all three,
   * and asking the network costs more than asking the ground.
   */
  private noRoad(x: number, y: number, w: number, h: number): boolean {
    const net = this.net; if (!net) return true;
    const hits: ReturnType<RoadSource['hitsAt']> = [];
    const onRoad = (tx: number, ty: number): boolean => { for (const hit of net.hitsAt(tx, ty, hits)) if (Math.abs(hit.off) <= hit.road.width / 2 + (hit.road.sidewalk ?? 0) + 1) return true; return false; };
    for (const ty of [y, y + Math.floor(h / 2), y + h]) for (let tx = x; tx <= x + w; tx += 3) if (onRoad(tx, ty)) return false;
    for (const tx of [x, x + Math.floor(w / 2), x + w]) for (let ty = y; ty <= y + h; ty += 3) if (onRoad(tx, ty)) return false;
    return true;
  }

  /** every repeating copy whose rect meets this one */
  private repeatingIn(x0: number, y0: number, x1: number, y1: number, probe = true): Place[] {
    const out: Place[] = [];
    for (const p of this.repeating) out.push(...this.repeatingOf(p, x0, y0, x1, y1, probe));
    return out;
  }
  /** the copies of one repeating place whose rect meets this one */
  private repeatingOf(p: Place, x0: number, y0: number, x1: number, y1: number, probe = true): Place[] {
    const out: Place[] = [];
    const every = p.every!, m = p.atTown ? 1 : 0; // (a town-edge copy can stand past its cell's line: the neighbours are asked too)
    const gx0 = Math.floor((x0 - p.w) / every) - m, gy0 = Math.floor((y0 - p.h) / every) - m;
    const gx1 = Math.floor(x1 / every) + m, gy1 = Math.floor(y1 / every) + m;
    for (let gy = gy0; gy <= gy1; gy++) for (let gx = gx0; gx <= gx1; gx++) {
      const c = this.copyAt(p, gx, gy, probe);
      if (c && c.x < x1 && c.x + c.w > x0 && c.y < y1 && c.y + c.h > y0) out.push(c);
    }
    return out;
  }

  /** Places intersecting a world rect. */
  in(x0: number, y0: number, x1: number, y1: number, probe = true): Place[] {
    const out = new Set<Place>(this.repeatingIn(x0, y0, x1, y1, probe));
    if (this.all.length) {
      const cx0 = Math.floor(x0 / CELL), cy0 = Math.floor(y0 / CELL), cx1 = Math.floor((x1 - 1) / CELL), cy1 = Math.floor((y1 - 1) / CELL);
      for (let cy = cy0; cy <= cy1; cy++) for (let cx = cx0; cx <= cx1; cx++) {
        for (const p of this.cells.get(`${cx},${cy}`) ?? []) if (p.x < x1 && p.x + p.w > x0 && p.y < y1 && p.y + p.h > y0) out.add(p);
      }
    }
    return [...out];
  }

  /** (Sep 25: the jerking at speed) this index lives on a main thread whose generator workers find the copies (`learn`): the
   *  ground's painting asks `at` without probing — a stroke reaching into a cell nobody has generated yet ran the half-second
   *  probe in the middle of a frame (a 56 ms slice of one chunk's ground on the drive audit) */
  learnOnly = false;
  /** The place containing a tile, if any (first match); `probe` false: only the copies already known. */
  at(x: number, y: number, probe = true): Place | undefined {
    for (const p of this.cells.get(`${Math.floor(x / CELL)},${Math.floor(y / CELL)}`) ?? []) if (x >= p.x && x < p.x + p.w && y >= p.y && y < p.y + p.h) return p;
    for (const p of this.repeating) {
      const every = p.every!, m = p.atTown ? 1 : 0;
      for (let gy = Math.floor((y - p.h) / every) - m; gy <= Math.floor(y / every) + m; gy++) for (let gx = Math.floor((x - p.w) / every) - m; gx <= Math.floor(x / every) + m; gx++) {
        const c = this.copyAt(p, gx, gy, probe && !p.remote); // (a remote place's probe is seconds of network: only a chunk's own `in` pays it, never a tile's question on the main thread)
        if (c && x >= c.x && x < c.x + c.w && y >= c.y && y < c.y + c.h) return c;
      }
    }
    return undefined;
  }
}
